import React, { useLayoutEffect, useRef } from 'react';

interface GlassFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** 唯一字段 ID，同时用于无障碍错误关联。 */
  id: string;
  /** 需要交互的区号选择放在 leading，不能藏在 aria-hidden 图标中。 */
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  error?: string;
}

/** 登录字段统一承载浮动标签、区号、操作和错误，保持验证码与密码框的布局一致。 */
export function GlassField({
  leading,
  trailing,
  error,
  id,
  ...input
}: GlassFieldProps) {
  return (
    <div className={`login-field${error ? ' is-invalid' : ''}`}>
      <div className={`login-input${input.disabled ? ' is-disabled' : ''}`}>
        {leading}
        <span className="login-floating-field">
          <input
            {...input}
            placeholder=" "
            id={id}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : undefined}
          />
          <label htmlFor={id}>{input['aria-label'] || input.placeholder}</label>
        </span>
        {trailing}
      </div>
      {error && (
        <p className="login-error" id={`${id}-error`} aria-live="polite">
          {error}
        </p>
      )}
    </div>
  );
}

/** 量测卡片内部实际高度，动画结束后恢复 auto，兼容错误消息、语言和窗口变化。 */
export function GlassCard({ children }: { children: React.ReactNode }) {
  const card = useRef<HTMLElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const lightFrame = useRef(0);
  /** 只在精确指针且未减少动态效果时更新位置，避免触屏和高频事件触发 React 渲染。 */
  const moveLight = (event: React.PointerEvent<HTMLElement>) => {
    if (
      !window.matchMedia(
        '(hover: hover) and (prefers-reduced-motion: no-preference)',
      ).matches
    )
      return;
    const { clientX, clientY } = event;
    cancelAnimationFrame(lightFrame.current);
    lightFrame.current = requestAnimationFrame(() => {
      const element = card.current;
      if (!element) return;
      const bounds = element.getBoundingClientRect();
      element.style.setProperty('--mx', `${clientX - bounds.left}px`);
      element.style.setProperty('--my', `${clientY - bounds.top}px`);
    });
  };
  useLayoutEffect(() => {
    const outer = card.current;
    const inner = content.current;
    if (!outer || !inner) return;
    let previous = inner.getBoundingClientRect().height;
    let frame = 0;
    let fallback: ReturnType<typeof setTimeout> | undefined;
    const reset = () => {
      outer.style.height = 'auto';
      outer.classList.remove('is-resizing');
    };
    const observer = new ResizeObserver(() => {
      const next = inner.getBoundingClientRect().height;
      if (Math.abs(next - previous) < 1) return;
      cancelAnimationFrame(frame);
      clearTimeout(fallback);
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        // 首次变更用上一次量测高度；动画中再次变化则从当前可见高度继续。
        const start =
          outer.style.height && outer.style.height !== 'auto'
            ? outer.getBoundingClientRect().height
            : previous + 2;
        outer.style.height = `${start}px`;
        outer.classList.add('is-resizing');
        frame = requestAnimationFrame(() => {
          outer.style.height = `${next + 2}px`;
        });
        fallback = setTimeout(reset, 550);
      } else reset();
      previous = next;
    });
    observer.observe(inner);
    const finish = (event: TransitionEvent) => {
      if (event.target === outer && event.propertyName === 'height') reset();
    };
    outer.addEventListener('transitionend', finish);
    return () => {
      cancelAnimationFrame(lightFrame.current);
      observer.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(fallback);
      outer.removeEventListener('transitionend', finish);
    };
  }, []);
  return (
    <section
      className="login-card"
      ref={card}
      aria-labelledby="login-brand"
      onPointerMove={moveLight}
    >
      <span className="login-card-highlight" aria-hidden="true" />
      <span className="login-card-glow" aria-hidden="true" />
      <span className="login-card-edge" aria-hidden="true" />
      <div className="login-card-content" ref={content}>
        {children}
      </div>
    </section>
  );
}

/** 使用仓库现有微信品牌图，不额外引入图标依赖。 */
export function WeChatIcon() {
  return (
    <img src="/wechat.svg" width={20} height={20} alt="" aria-hidden="true" />
  );
}

/** 可访问的提交等待状态，外层按钮提供具体动作名称。 */
export function LoginSpinner() {
  return <span className="login-spinner" aria-hidden="true" />;
}
